import { Box, makeStyles, Theme, Typography } from '@freighthub/silhouette-core'
import cn from 'classnames'
import { FC } from 'react'

import { IShipTpEvent } from '../../domain/TransportPlanEvent'
import { TPdadStepIconType } from '../PdadIcon'
import { TpEventTimestampRangeWithProvider } from '../TpRange'
import { MilestoneIcon, Progress } from './TpProgressBar'

export type TpProgressSegmentProps = {
  icon: TPdadStepIconType
  size: number
  tooltip: string
  isIconDisabled: boolean
  isStepDisabled: boolean
  isStepComplete: boolean
  percentWidth: number
  locationText: string
  isLocationHighlighted: boolean
  events: IShipTpEvent[]
  isLast: boolean
  showDelay: boolean
}

const useStyles = makeStyles<Theme, Pick<TpProgressSegmentProps, 'isLocationHighlighted'>>((theme) => ({
  progressSegment: {
    flex: 1,
    minWidth: 0,
  },
  progressText: {
    minHeight: 20,
    padding: theme.spacing(0, 1),
    whiteSpace: 'nowrap',
    textAlign: 'center',
    transform: 'translateX(calc(-50% + 16px))',
  },
  progressDateCrd: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  progressLocation: ({ isLocationHighlighted }) => ({
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: isLocationHighlighted ? theme.palette.primary.main : theme.palette.text.primary,
  }),
  lastSegment: {
    flex: '0 1 15%',
  },
}))

export const TpProgressSegment: FC<TpProgressSegmentProps> = ({
  isLocationHighlighted,
  icon,
  size,
  tooltip,
  isIconDisabled,
  isStepDisabled,
  isStepComplete,
  percentWidth,
  locationText,
  events,
  isLast,
  showDelay,
}) => {
  const classes = useStyles({ isLocationHighlighted })

  const hasDate: boolean = Boolean(events?.length) && events.every((event) => !!event && !!event.aggregatedTimestamp)

  return (
    <Box className={cn(classes.progressSegment, isLast && classes.lastSegment)}>
      <Typography title={locationText} className={cn(classes.progressText, classes.progressLocation)}>
        {locationText}
      </Typography>
      <Box display="flex" justifyContent="space-between" alignItems="center" flex={1}>
        <MilestoneIcon
          tooltip={tooltip}
          size={size}
          isStepComplete={isStepComplete}
          isIconDisabled={isIconDisabled}
          icon={icon}
        />
        {!isLast && <Progress percentage={isStepDisabled ? 100 : percentWidth} isStepDisabled={isStepDisabled} />}
      </Box>
      {!hasDate && (
        <Box className={classes.progressText}>
          <Typography>{tooltip}</Typography>
        </Box>
      )}
      {hasDate && (
        <Box className={classes.progressText}>
          <Typography>{tooltip}: </Typography>
          <TpEventTimestampRangeWithProvider
            events={events}
            showDelay={showDelay}
            showTooltip={showDelay}
            showReason={false}
            showEllipsis
            useSingleLine
            useIntervalDescription
          />
        </Box>
      )}
    </Box>
  )
}
